import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { TIDYCAL_URL, scrollToSection } from "@/constants";

const PROGRAMS = [
  {
    title: "AI-Ready Workforce",
    intro: "A practical professional development experience that helps employees understand and confidently use artificial intelligence in their everyday work.",
    lead: "Participants explore:",
    points: ["Practical AI tools", "Prompting techniques", "Workflow automation", "Research and writing", "Productivity", "Responsible AI use", "Privacy and security", "Department-specific opportunities"],
    ideal: "Companies · Government Agencies · Associations · Nonprofits",
    engagement: "Half-day · Full-day · Multi-session program",
    cta: "Bring AI-Ready Workforce to Your Team",
    href: "#contact",
    testid: "program-workforce",
  },
  {
    title: "AI-Ready Educator",
    intro: "Help teachers and school leaders understand how AI can support instruction while reducing repetitive administrative work.",
    lead: "Participants learn practical ways to use AI for:",
    points: ["Lesson planning", "Curriculum development", "Differentiated instruction", "Parent communication", "Rubrics and assessments", "Research", "Student engagement", "Administrative productivity", "Responsible classroom AI use"],
    ideal: "School Districts · Educators · Administrators · Instructional Coaches",
    engagement: null,
    cta: "Explore Educator Professional Development",
    href: "#contact",
    testid: "program-educator",
  },
  {
    title: "Technology Leadership for the AI Era",
    intro: "A strategy-focused experience for executives and organizational leaders responsible for navigating rapid technological change.",
    lead: "Topics include:",
    points: ["AI strategy", "Workforce transformation", "Automation opportunities", "Technology adoption", "Organizational resistance", "Future skills", "AI governance", "Innovation culture"],
    ideal: "Executives · Superintendents · Department Leaders · Boards · Leadership Retreats",
    engagement: null,
    cta: "Schedule a Leadership Session",
    href: TIDYCAL_URL,
    testid: "program-leadership",
  },
];

export default function Programs() {
  return (
    <section id="solutions" data-testid="programs-section" className="py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.p initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, ease: "easeOut" }} className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-accent">
          Professional Development
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }} className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-6 max-w-4xl">
          Training designed to create adoption — <span className="italic text-primary">not just awareness.</span>
        </motion.h2>

        <div className="mt-24 space-y-16">
          {PROGRAMS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              data-testid={p.testid}
              className="rounded-[2rem] border border-[#D8D4CA] bg-muted/40 p-8 md:p-14 grid grid-cols-1 lg:grid-cols-12 gap-12"
            >
              <div className="lg:col-span-5">
                <span className="font-display text-5xl text-outline select-none">0{i + 1}</span>
                <h3 className="font-display text-3xl sm:text-4xl tracking-tight mt-4">{p.title}</h3>
                <p className="mt-5 text-base sm:text-lg leading-relaxed font-light text-foreground/80">{p.intro}</p>
                <div className="mt-8 space-y-3 text-sm">
                  <p><span className="font-bold">Ideal for: </span><span className="text-foreground/70">{p.ideal}</span></p>
                  {p.engagement && (
                    <p><span className="font-bold">Typical Engagement: </span><span className="text-foreground/70">{p.engagement}</span></p>
                  )}
                </div>
                {p.href.startsWith("#") ? (
                  <button
                    onClick={() => scrollToSection(p.href)}
                    data-testid={`${p.testid}-button`}
                    className="mt-10 inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-7 py-3.5 text-sm font-semibold hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/20 transition-transform duration-300"
                  >
                    {p.cta} <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${p.testid}-button`}
                    className="mt-10 inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-7 py-3.5 text-sm font-semibold hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/20 transition-transform duration-300"
                  >
                    {p.cta} <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
              <div className="lg:col-span-7 lg:border-l lg:border-[#D8D4CA] lg:pl-12">
                <p className="text-xs tracking-[0.25em] uppercase font-bold text-foreground/50 mb-8">{p.lead}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-3 text-base text-foreground/80">
                      <Check className="w-4 h-4 text-primary mt-1 shrink-0" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
